import { useCallback, useEffect, useRef, useState } from "react";
import {
  ACTIVE_DOCK_PANEL_ORDER,
  isActiveDockPanelKey,
  type DockPanelKey,
} from "../components/dockPanelLabels";

/**
 * Dock layout + workspace toolbar state extracted from EEGViewer to keep the
 * main component focused on rendering. Owns dock pane sizing/collapse state,
 * dock panel visibility/order, the bottom/right/clinical resize drag
 * lifecycle, and the output-toolbar workspace section (signal/dual/review/
 * filters/settings) open/pinned/auto-hide behavior.
 */

export const DEFAULT_DOCK_LAYOUT = {
  bottomHeightPx: 260,
  rightWidthPx: 300,
  clinicalWidthPx: 320,
  bottomCollapsed: true,
  rightCollapsed: true,
};
export const CLINICAL_DOCK_MIN_WIDTH_PX = 288;
export const CLINICAL_DOCK_MAX_WIDTH_PX = 440;
export const RIGHT_DOCK_MIN_WIDTH_PX = 220;
export const RIGHT_DOCK_MAX_WIDTH_PX = 460;

export const DEFAULT_DOCK_PANEL_VISIBILITY: Record<DockPanelKey, boolean> = {
  annotation_timeline: false,
  annotation_list: true,
  event_detection: true,
  clinical_timeline: true,
  qeeg_trends: true,
  artifact_quality: true,
  review_accel: true,
  clinical_collab: true,
  spectrogram: true,
  trend_correlation: true,
  psd_analysis: true,
  connectivity_analysis: true,
  hfo_analysis: true,
  live_scalp: true,
  ieeg_localize: true,
};

export const DEFAULT_DOCK_PANEL_ORDER: DockPanelKey[] = ACTIVE_DOCK_PANEL_ORDER;
export const DOCK_BOTTOM_COLLAPSED_STORAGE_KEY = "eeg-dock-bottom-collapsed";
export const DOCK_RIGHT_COLLAPSED_STORAGE_KEY = "eeg-dock-right-collapsed";
export const EVENTS_TRENDS_DOCK_PLACEMENT_STORAGE_KEY =
  "eeg-events-trends-dock-placement";

export type EventsTrendsDockPlacement = "bottom" | "right" | "undocked";
export const CLINICAL_DOCK_WIDTH_STORAGE_KEY = "eeg-clinical-dock-width";
export const TOOLBAR_REVIEW_DRAWER_OPEN_STORAGE_KEY =
  "eeg-toolbar-review-drawer-open";
export const TOOLBAR_DUAL_DRAWER_OPEN_STORAGE_KEY =
  "eeg-toolbar-dual-drawer-open";
export const TOOLBAR_AUTO_HIDE_STORAGE_KEY = "eeg-toolbar-auto-hide";
export const WORKSPACE_CONTROLS_PEEK_HIDE_DELAY_MS = 124;
export const TOOLBAR_SIGNAL_CARD_VISIBLE_STORAGE_KEY =
  "eeg-toolbar-signal-card-visible";
export const OUTPUT_TOOLBAR_SECTION_ORDER_STORAGE_KEY =
  "eeg-toolbar-output-section-order";
export const REVIEW_TOOLS_ITEM_ORDER_STORAGE_KEY =
  "eeg-toolbar-review-tools-item-order";

export type OutputToolbarSectionKey =
  | "clinical"
  | "signal"
  | "dual"
  | "settings"
  | "review"
  | "filters";

export const OUTPUT_TOOLBAR_SECTION_ORDER_DEFAULT: OutputToolbarSectionKey[] = [
  "clinical",
  "signal",
  "dual",
  "settings",
  "review",
  "filters",
];

export const REVIEW_TOOLS_ITEM_ORDER_DEFAULT = [
  "measure",
  "polarity",
  "inspector",
  "channel-review",
  "hp",
  "lp",
  "notch",
  "crosshair",
  "calibration",
  "compare",
  "export",
  "print",
  "event-count",
] as const;

export type ReviewToolsItemKey =
  (typeof REVIEW_TOOLS_ITEM_ORDER_DEFAULT)[number];

export function readDockCollapsedPreferenceOverride(
  storageKey: string,
): boolean | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch {
    // Ignore storage access failures.
  }
  return null;
}

export function readDockCollapsedPreference(
  storageKey: string,
  fallback: boolean,
): boolean {
  return readDockCollapsedPreferenceOverride(storageKey) ?? fallback;
}

export function readEventsTrendsDockPlacement(): EventsTrendsDockPlacement {
  try {
    const raw = localStorage.getItem(EVENTS_TRENDS_DOCK_PLACEMENT_STORAGE_KEY);
    if (raw === "bottom" || raw === "right" || raw === "undocked") {
      return raw;
    }
  } catch {
    // Ignore storage access failures.
  }
  return "undocked";
}

export function readDockDimensionPreferenceOverride(
  storageKey: string,
): number | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const parsed = Number(raw);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  } catch {
    // Ignore storage access failures.
  }

  return null;
}

export function readDockDimensionPreference(
  storageKey: string,
  fallback: number,
  min: number,
  max: number,
): number {
  const stored = readDockDimensionPreferenceOverride(storageKey);
  const candidate = stored ?? fallback;
  return Math.max(min, Math.min(max, candidate));
}

export function normalizeOutputToolbarSectionOrder(
  value: unknown,
): OutputToolbarSectionKey[] {
  if (!Array.isArray(value)) {
    return OUTPUT_TOOLBAR_SECTION_ORDER_DEFAULT;
  }
  const allowed = new Set<OutputToolbarSectionKey>([
    "clinical",
    "signal",
    "dual",
    "settings",
    "review",
    "filters",
  ]);
  const normalized: OutputToolbarSectionKey[] = [];
  value.forEach((entry) => {
    if (typeof entry !== "string") return;
    if (!allowed.has(entry as OutputToolbarSectionKey)) return;
    const section = entry as OutputToolbarSectionKey;
    if (!normalized.includes(section)) {
      normalized.push(section);
    }
  });
  OUTPUT_TOOLBAR_SECTION_ORDER_DEFAULT.forEach((section, defaultIndex) => {
    if (!normalized.includes(section)) {
      normalized.splice(Math.min(defaultIndex, normalized.length), 0, section);
    }
  });
  return normalized;
}

export function normalizeReviewToolsItemOrder(
  value: unknown,
): ReviewToolsItemKey[] {
  if (!Array.isArray(value)) {
    return [...REVIEW_TOOLS_ITEM_ORDER_DEFAULT];
  }
  const allowed = new Set<ReviewToolsItemKey>(REVIEW_TOOLS_ITEM_ORDER_DEFAULT);
  const normalized: ReviewToolsItemKey[] = [];
  value.forEach((entry) => {
    if (typeof entry !== "string") return;
    if (!allowed.has(entry as ReviewToolsItemKey)) return;
    const item = entry as ReviewToolsItemKey;
    if (!normalized.includes(item)) {
      normalized.push(item);
    }
  });
  REVIEW_TOOLS_ITEM_ORDER_DEFAULT.forEach((item) => {
    if (!normalized.includes(item)) {
      normalized.push(item);
    }
  });
  return normalized;
}

export interface UseEEGViewerDockLayoutOptions {
  /** Whether the settings/config panel is currently open (owned outside this hook). */
  showConfigPanel: boolean;
  /** Panel keys currently detached into their own popped-out window. */
  poppedOutPanels: ReadonlySet<DockPanelKey>;
  /** Currently selected bottom-dock tab (owned outside this hook). */
  dockBottomActiveTab: DockPanelKey | null;
}

export function useEEGViewerDockLayout(options: UseEEGViewerDockLayoutOptions) {
  const { showConfigPanel, poppedOutPanels, dockBottomActiveTab } = options;

  const [dockBottomHeightPx, setDockBottomHeightPx] = useState<number>(
    DEFAULT_DOCK_LAYOUT.bottomHeightPx,
  );
  const [dockRightWidthPx, setDockRightWidthPx] = useState<number>(
    DEFAULT_DOCK_LAYOUT.rightWidthPx,
  );
  const [annotationPanelWidthPx, setAnnotationPanelWidthPx] = useState<number>(
    () =>
      readDockDimensionPreference(
        CLINICAL_DOCK_WIDTH_STORAGE_KEY,
        DEFAULT_DOCK_LAYOUT.clinicalWidthPx,
        CLINICAL_DOCK_MIN_WIDTH_PX,
        CLINICAL_DOCK_MAX_WIDTH_PX,
      ),
  );
  const [dockBottomCollapsed, setDockBottomCollapsed] = useState<boolean>(() =>
    readDockCollapsedPreference(
      DOCK_BOTTOM_COLLAPSED_STORAGE_KEY,
      DEFAULT_DOCK_LAYOUT.bottomCollapsed,
    ),
  );
  const [dockRightCollapsed, setDockRightCollapsed] = useState<boolean>(() =>
    readDockCollapsedPreference(
      DOCK_RIGHT_COLLAPSED_STORAGE_KEY,
      DEFAULT_DOCK_LAYOUT.rightCollapsed,
    ),
  );
  const [eventsTrendsDockPlacement, setEventsTrendsDockPlacement] =
    useState<EventsTrendsDockPlacement>(() => readEventsTrendsDockPlacement());
  const [filterRowOpen, setFilterRowOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem("eeg-filter-row-open") === "true";
    } catch {
      return false;
    }
  });
  const [reviewToolsDrawerOpen, setReviewToolsDrawerOpen] = useState<boolean>(
    () => {
      try {
        return (
          localStorage.getItem(TOOLBAR_REVIEW_DRAWER_OPEN_STORAGE_KEY) ===
          "true"
        );
      } catch {
        return false;
      }
    },
  );
  const [dualCompareDrawerOpen, setDualCompareDrawerOpen] = useState<boolean>(
    () => {
      try {
        return (
          localStorage.getItem(TOOLBAR_DUAL_DRAWER_OPEN_STORAGE_KEY) === "true"
        );
      } catch {
        return false;
      }
    },
  );
  const [toolbarAutoHide, setToolbarAutoHide] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(TOOLBAR_AUTO_HIDE_STORAGE_KEY);
      if (stored === null) return process.env.NODE_ENV === "test" ? false : true;
      return stored === "true";
    } catch {
      return process.env.NODE_ENV === "test" ? false : true;
    }
  });
  const [signalCardVisible, setSignalCardVisible] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(
        TOOLBAR_SIGNAL_CARD_VISIBLE_STORAGE_KEY,
      );
      if (stored === "false") return false;
      return true;
    } catch {
      return true;
    }
  });
  const [outputToolbarSectionOrder, setOutputToolbarSectionOrder] = useState<
    OutputToolbarSectionKey[]
  >(() => {
    try {
      const raw = localStorage.getItem(
        OUTPUT_TOOLBAR_SECTION_ORDER_STORAGE_KEY,
      );
      if (!raw) return OUTPUT_TOOLBAR_SECTION_ORDER_DEFAULT;
      return normalizeOutputToolbarSectionOrder(JSON.parse(raw));
    } catch {
      return OUTPUT_TOOLBAR_SECTION_ORDER_DEFAULT;
    }
  });
  const [draggingOutputToolbarSection, setDraggingOutputToolbarSection] =
    useState<OutputToolbarSectionKey | null>(null);
  const [reviewToolsItemOrder, setReviewToolsItemOrder] = useState<
    ReviewToolsItemKey[]
  >(() => {
    try {
      const raw = localStorage.getItem(REVIEW_TOOLS_ITEM_ORDER_STORAGE_KEY);
      if (!raw) return [...REVIEW_TOOLS_ITEM_ORDER_DEFAULT];
      return normalizeReviewToolsItemOrder(JSON.parse(raw));
    } catch {
      return [...REVIEW_TOOLS_ITEM_ORDER_DEFAULT];
    }
  });
  const [workspaceControlsPinned, setWorkspaceControlsPinned] =
    useState<boolean>(false);
  const [workspaceControlsPeekOpen, setWorkspaceControlsPeekOpen] =
    useState<boolean>(false);
  const workspaceControlsPeekHideTimerRef = useRef<number | null>(null);
  const [dockPanelVisibility, setDockPanelVisibility] = useState<
    Record<DockPanelKey, boolean>
  >(DEFAULT_DOCK_PANEL_VISIBILITY);
  const [dockPanelOrder, setDockPanelOrder] = useState<DockPanelKey[]>(
    DEFAULT_DOCK_PANEL_ORDER,
  );
  const [activeDockResize, setActiveDockResize] = useState<
    "bottom" | "right" | "clinical" | null
  >(null);
  const dockResizeStartRef = useRef<{
    x: number;
    y: number;
    annotationPanelWidthPx: number;
    rightWidthPx: number;
    bottomHeightPx: number;
  } | null>(null);

  // EEG-UX-005: only one workspace toolbar section (signal/dual/review/
  // filters) may be open at a time; reconcile whenever any of them changes.
  useEffect(() => {
    const openWorkspaceSections: OutputToolbarSectionKey[] = [];
    if (signalCardVisible) openWorkspaceSections.push("signal");
    if (dualCompareDrawerOpen) openWorkspaceSections.push("dual");
    if (reviewToolsDrawerOpen) openWorkspaceSections.push("review");

    if (openWorkspaceSections.length <= 1) {
      if (
        openWorkspaceSections.length === 0 &&
        !filterRowOpen &&
        !showConfigPanel
      ) {
        setSignalCardVisible(true);
      }
      return;
    }

    const preferredSection =
      outputToolbarSectionOrder.find((section) =>
        openWorkspaceSections.includes(section),
      ) || openWorkspaceSections[0];

    setSignalCardVisible(preferredSection === "signal");
    setDualCompareDrawerOpen(preferredSection === "dual");
    setReviewToolsDrawerOpen(preferredSection === "review");
  }, [
    dualCompareDrawerOpen,
    filterRowOpen,
    outputToolbarSectionOrder,
    reviewToolsDrawerOpen,
    showConfigPanel,
    signalCardVisible,
  ]);

  useEffect(() => {
    try {
      localStorage.setItem("eeg-filter-row-open", String(filterRowOpen));
    } catch {
      /* ignore */
    }
  }, [filterRowOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(
        TOOLBAR_REVIEW_DRAWER_OPEN_STORAGE_KEY,
        String(reviewToolsDrawerOpen),
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [reviewToolsDrawerOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(
        TOOLBAR_DUAL_DRAWER_OPEN_STORAGE_KEY,
        String(dualCompareDrawerOpen),
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [dualCompareDrawerOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(
        TOOLBAR_AUTO_HIDE_STORAGE_KEY,
        String(toolbarAutoHide),
      );
    } catch {
      // Ignore storage access failures.
    }
    if (!toolbarAutoHide) {
      setWorkspaceControlsPinned(false);
      setWorkspaceControlsPeekOpen(false);
    }
  }, [toolbarAutoHide]);

  useEffect(() => {
    try {
      localStorage.setItem(
        TOOLBAR_SIGNAL_CARD_VISIBLE_STORAGE_KEY,
        String(signalCardVisible),
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [signalCardVisible]);

  useEffect(() => {
    try {
      localStorage.setItem(
        OUTPUT_TOOLBAR_SECTION_ORDER_STORAGE_KEY,
        JSON.stringify(outputToolbarSectionOrder),
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [outputToolbarSectionOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(
        REVIEW_TOOLS_ITEM_ORDER_STORAGE_KEY,
        JSON.stringify(reviewToolsItemOrder),
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [reviewToolsItemOrder]);

  useEffect(() => {
    try {
      localStorage.setItem(
        DOCK_BOTTOM_COLLAPSED_STORAGE_KEY,
        String(dockBottomCollapsed),
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [dockBottomCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(
        EVENTS_TRENDS_DOCK_PLACEMENT_STORAGE_KEY,
        eventsTrendsDockPlacement,
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [eventsTrendsDockPlacement]);

  useEffect(() => {
    try {
      localStorage.setItem(
        DOCK_RIGHT_COLLAPSED_STORAGE_KEY,
        String(dockRightCollapsed),
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [dockRightCollapsed]);

  useEffect(() => {
    try {
      localStorage.setItem(
        CLINICAL_DOCK_WIDTH_STORAGE_KEY,
        String(annotationPanelWidthPx),
      );
    } catch {
      // Ignore storage access failures.
    }
  }, [annotationPanelWidthPx]);

  const isDockPanelVisible = useCallback(
    (panelKey: DockPanelKey) => {
      if (!isActiveDockPanelKey(panelKey)) return false;
      if (!dockPanelVisibility[panelKey]) return false;
      if (poppedOutPanels.has(panelKey)) return true;

      const visibleOrderedPanels = dockPanelOrder.filter(
        (candidateKey) =>
          isActiveDockPanelKey(candidateKey) &&
          Object.prototype.hasOwnProperty.call(
            dockPanelVisibility,
            candidateKey,
          ) &&
          dockPanelVisibility[candidateKey],
      );
      const rightPanelKey = visibleOrderedPanels[0] || null;
      if (rightPanelKey === panelKey) {
        return !dockRightCollapsed;
      }

      const bottomPanels = visibleOrderedPanels.slice(1);
      const activeBottomPanel =
        dockBottomActiveTab && bottomPanels.includes(dockBottomActiveTab)
          ? dockBottomActiveTab
          : (bottomPanels[0] ?? null);

      return !dockBottomCollapsed && activeBottomPanel === panelKey;
    },
    [
      dockBottomActiveTab,
      dockBottomCollapsed,
      dockPanelOrder,
      dockPanelVisibility,
      dockRightCollapsed,
      poppedOutPanels,
    ],
  );

  const moveDockPanel = useCallback(
    (panelKey: DockPanelKey, direction: "left" | "right") => {
      setDockPanelOrder((currentOrder) => {
        const index = currentOrder.indexOf(panelKey);
        if (index < 0) return currentOrder;
        const nextIndex = direction === "left" ? index - 1 : index + 1;
        if (nextIndex < 0 || nextIndex >= currentOrder.length) {
          return currentOrder;
        }
        const nextOrder = [...currentOrder];
        const [item] = nextOrder.splice(index, 1);
        nextOrder.splice(nextIndex, 0, item);
        return nextOrder;
      });
    },
    [],
  );

  const toggleDockPanelVisibility = useCallback((panelKey: DockPanelKey) => {
    setDockPanelVisibility((current) => ({
      ...current,
      [panelKey]: !current[panelKey],
    }));
  }, []);

  useEffect(() => {
    if (!activeDockResize) {
      dockResizeStartRef.current = null;
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      const start = dockResizeStartRef.current;
      if (!start) return;

      if (activeDockResize === "clinical") {
        const deltaX = start.x - event.clientX;
        const nextWidth = Math.max(
          CLINICAL_DOCK_MIN_WIDTH_PX,
          Math.min(
            CLINICAL_DOCK_MAX_WIDTH_PX,
            start.annotationPanelWidthPx + deltaX,
          ),
        );
        setAnnotationPanelWidthPx(nextWidth);
        return;
      }

      if (activeDockResize === "right") {
        const deltaX = start.x - event.clientX;
        const nextWidth = Math.max(
          RIGHT_DOCK_MIN_WIDTH_PX,
          Math.min(RIGHT_DOCK_MAX_WIDTH_PX, start.rightWidthPx + deltaX),
        );
        setDockRightWidthPx(nextWidth);
        return;
      }

      const deltaY = start.y - event.clientY;
      const nextHeight = Math.max(
        160,
        Math.min(540, start.bottomHeightPx + deltaY),
      );
      setDockBottomHeightPx(nextHeight);
    };

    const handleMouseUp = () => {
      setActiveDockResize(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [activeDockResize]);

  const handleDockResizeStart = useCallback(
    (
      resizeTarget: "bottom" | "right" | "clinical",
      event: React.MouseEvent,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      dockResizeStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        annotationPanelWidthPx,
        rightWidthPx: dockRightWidthPx,
        bottomHeightPx: dockBottomHeightPx,
      };
      setActiveDockResize(resizeTarget);
    },
    [annotationPanelWidthPx, dockBottomHeightPx, dockRightWidthPx],
  );

  const cancelWorkspaceControlsPeekHide = useCallback(() => {
    if (workspaceControlsPeekHideTimerRef.current) {
      window.clearTimeout(workspaceControlsPeekHideTimerRef.current);
      workspaceControlsPeekHideTimerRef.current = null;
    }
  }, []);

  const toggleToolbarAutoHide = useCallback(() => {
    setToolbarAutoHide((current) => !current);
    setWorkspaceControlsPinned(false);
    setWorkspaceControlsPeekOpen(false);
    cancelWorkspaceControlsPeekHide();
  }, [cancelWorkspaceControlsPeekHide]);

  const getWorkspaceControlsPeekHideDelayMs = useCallback(() => {
    if (typeof window === "undefined") return 0;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return 0;
    }
    if (
      document.documentElement.getAttribute("data-reduced-motion") === "true"
    ) {
      return 0;
    }
    return WORKSPACE_CONTROLS_PEEK_HIDE_DELAY_MS;
  }, []);

  const revealWorkspaceControlsPeek = useCallback(() => {
    if (!toolbarAutoHide || workspaceControlsPinned) return;
    cancelWorkspaceControlsPeekHide();
    setWorkspaceControlsPeekOpen(true);
  }, [
    cancelWorkspaceControlsPeekHide,
    toolbarAutoHide,
    workspaceControlsPinned,
  ]);

  const scheduleHideWorkspaceControlsPeek = useCallback(() => {
    if (!toolbarAutoHide || workspaceControlsPinned) return;
    cancelWorkspaceControlsPeekHide();
    workspaceControlsPeekHideTimerRef.current = window.setTimeout(() => {
      workspaceControlsPeekHideTimerRef.current = null;
      setWorkspaceControlsPeekOpen(false);
    }, getWorkspaceControlsPeekHideDelayMs());
  }, [
    cancelWorkspaceControlsPeekHide,
    getWorkspaceControlsPeekHideDelayMs,
    toolbarAutoHide,
    workspaceControlsPinned,
  ]);

  const toggleWorkspaceControls = useCallback(() => {
    if (toolbarAutoHide) {
      if (workspaceControlsPinned) {
        setWorkspaceControlsPinned(false);
        setWorkspaceControlsPeekOpen(false);
        cancelWorkspaceControlsPeekHide();
        return;
      }
      setWorkspaceControlsPinned(true);
      setWorkspaceControlsPeekOpen(true);
      cancelWorkspaceControlsPeekHide();
      return;
    }
    setToolbarAutoHide(true);
    setWorkspaceControlsPinned(true);
    setWorkspaceControlsPeekOpen(true);
    cancelWorkspaceControlsPeekHide();
  }, [
    cancelWorkspaceControlsPeekHide,
    toolbarAutoHide,
    workspaceControlsPinned,
  ]);

  const revealToolbarPeek = useCallback(() => {
    if (toolbarAutoHide) {
      setWorkspaceControlsPinned(true);
      setWorkspaceControlsPeekOpen(true);
      cancelWorkspaceControlsPeekHide();
      return;
    }
    setToolbarAutoHide(true);
    setWorkspaceControlsPinned(true);
    setWorkspaceControlsPeekOpen(true);
    cancelWorkspaceControlsPeekHide();
  }, [cancelWorkspaceControlsPeekHide, toolbarAutoHide]);

  useEffect(
    () => () => cancelWorkspaceControlsPeekHide(),
    [cancelWorkspaceControlsPeekHide],
  );

  const moveOutputToolbarSection = useCallback(
    (source: OutputToolbarSectionKey, target: OutputToolbarSectionKey) => {
      if (source === target) return;
      setOutputToolbarSectionOrder((current) => {
        const fromIndex = current.indexOf(source);
        const toIndex = current.indexOf(target);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
          return current;
        }
        const next = [...current];
        next.splice(fromIndex, 1);
        next.splice(toIndex, 0, source);
        return normalizeOutputToolbarSectionOrder(next);
      });
    },
    [],
  );

  const moveReviewToolsItem = useCallback(
    (source: string, target: string) => {
      if (source === target) return;
      setReviewToolsItemOrder((current) => {
        const fromIndex = current.indexOf(source as ReviewToolsItemKey);
        const toIndex = current.indexOf(target as ReviewToolsItemKey);
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
          return current;
        }
        const next = [...current];
        next.splice(fromIndex, 1);
        next.splice(toIndex, 0, source as ReviewToolsItemKey);
        return normalizeReviewToolsItemOrder(next);
      });
    },
    [],
  );

  const getLeftCardOrder = useCallback(
    (section: OutputToolbarSectionKey): number => {
      const index = outputToolbarSectionOrder.indexOf(section);
      if (index < 0) return OUTPUT_TOOLBAR_SECTION_ORDER_DEFAULT.length + 1;
      return index + 1;
    },
    [outputToolbarSectionOrder],
  );

  return {
    dockBottomHeightPx,
    setDockBottomHeightPx,
    dockRightWidthPx,
    setDockRightWidthPx,
    annotationPanelWidthPx,
    setAnnotationPanelWidthPx,
    dockBottomCollapsed,
    setDockBottomCollapsed,
    dockRightCollapsed,
    setDockRightCollapsed,
    eventsTrendsDockPlacement,
    setEventsTrendsDockPlacement,
    filterRowOpen,
    setFilterRowOpen,
    reviewToolsDrawerOpen,
    setReviewToolsDrawerOpen,
    dualCompareDrawerOpen,
    setDualCompareDrawerOpen,
    toolbarAutoHide,
    setToolbarAutoHide,
    signalCardVisible,
    setSignalCardVisible,
    outputToolbarSectionOrder,
    setOutputToolbarSectionOrder,
    draggingOutputToolbarSection,
    setDraggingOutputToolbarSection,
    workspaceControlsPinned,
    setWorkspaceControlsPinned,
    workspaceControlsPeekOpen,
    setWorkspaceControlsPeekOpen,
    dockPanelVisibility,
    setDockPanelVisibility,
    dockPanelOrder,
    setDockPanelOrder,
    activeDockResize,
    setActiveDockResize,
    dockResizeStartRef,
    isDockPanelVisible,
    moveDockPanel,
    toggleDockPanelVisibility,
    handleDockResizeStart,
    cancelWorkspaceControlsPeekHide,
    toggleToolbarAutoHide,
    getWorkspaceControlsPeekHideDelayMs,
    revealWorkspaceControlsPeek,
    scheduleHideWorkspaceControlsPeek,
    toggleWorkspaceControls,
    revealToolbarPeek,
    moveOutputToolbarSection,
    getLeftCardOrder,
    reviewToolsItemOrder,
    moveReviewToolsItem,
  };
}
